import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import config from './config';
import { getTalkingHeadVoiceConfig } from './voicePreferences';
import {
  DEFAULT_AVATAR_ID,
  readStoredAvatar,
  resolveAvatar,
  storeSelectedAvatar,
} from './avatarCatalog';

const STEADY_POSE_MORPHS = { bodyRotateX: 0, bodyRotateY: 0, bodyRotateZ: 0 };

const DEFAULT_AVATAR_BASE_URL =
  'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@main/avatars/avaturn.glb';
const TALKING_HEAD_MODULE_URLS = [
  'https://cdn.jsdelivr.net/gh/met4citizen/TalkingHead@1.4/modules/talkinghead.mjs',
  'https://rawcdn.githack.com/met4citizen/TalkingHead/1.4/modules/talkinghead.mjs',
];
const MAX_AVATAR_LOAD_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [700, 1600];

let talkingHeadModulePromise = null;

function hasWebGLSupport() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    return !!(window.WebGLRenderingContext && context);
  } catch {
    return false;
  }
}

function isReadyPlayerUrl(url) {
  return typeof url === 'string' && /readyplayer\.me/i.test(url);
}

function isUnsupportedTalkingHeadAvatarError(error) {
  const message =
    typeof error?.message === 'string' ? error.message : `${error || ''}`;

  return /Avatar object Armature not found|Blend shapes not found|eyeBlinkLeft|eyeBlinkRight|Cannot read properties of undefined \(reading 'value'\)/i.test(
    message
  );
}

function extractObjectPathFromGcsUrl(urlValue) {
  if (typeof urlValue !== 'string' || !urlValue.trim()) {
    return null;
  }

  try {
    const parsed = new URL(urlValue.trim());
    if (parsed.hostname !== 'storage.googleapis.com') {
      return null;
    }

    const path = decodeURIComponent(parsed.pathname || '');
    const match = path.match(/^\/[^/]+\/(.+)$/);
    if (!match || !match[1]) {
      return null;
    }

    return match[1];
  } catch {
    return null;
  }
}

function buildBackendAvatarProxyUrl(objectPath) {
  if (typeof objectPath !== 'string' || !objectPath.trim()) {
    return null;
  }

  const userId = localStorage.getItem('userId');
  if (!userId) {
    return null;
  }

  const params = new URLSearchParams({
    userId: String(userId),
    path: objectPath,
  });
  return `${config.API_BASE_URL}/user-avatar/file?${params.toString()}`;
}

function buildAvatarUrl(baseUrl) {
  const rawUrl =
    typeof baseUrl === 'string' && baseUrl.trim()
      ? baseUrl.trim()
      : DEFAULT_AVATAR_BASE_URL;

  try {
    const baseOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : undefined;
    const url = baseOrigin ? new URL(rawUrl, baseOrigin) : new URL(rawUrl);

    const gcsObjectPath = extractObjectPathFromGcsUrl(url.toString());
    if (gcsObjectPath) {
      const proxyUrl = buildBackendAvatarProxyUrl(gcsObjectPath);
      if (proxyUrl) {
        return proxyUrl;
      }
    }

    if (isReadyPlayerUrl(url.toString())) {
      url.searchParams.set(
        'morphTargets',
        'ARKit,Oculus Visemes,mouthOpen,mouthSmile,eyesClosed,eyesLookUp,eyesLookDown'
      );
      url.searchParams.set('textureSizeLimit', '1024');
      url.searchParams.set('textureFormat', 'png');
    }
    return url.toString();
  } catch {
    return DEFAULT_AVATAR_BASE_URL;
  }
}

function importExternalModule(url) {
  return import(/* webpackIgnore: true */ url);
}

function loadTalkingHeadModule() {
  if (!talkingHeadModulePromise) {
    talkingHeadModulePromise = (async () => {
      let lastError = null;

      for (const moduleUrl of TALKING_HEAD_MODULE_URLS) {
        try {
          return await importExternalModule(moduleUrl);
        } catch (error) {
          lastError = error;
          console.warn(`Failed to load TalkingHead module from ${moduleUrl}`, error);
        }
      }

      throw lastError || new Error('Unable to load TalkingHead module from configured URLs.');
    })().catch((error) => {
      talkingHeadModulePromise = null;
      throw error;
    });
  }

  return talkingHeadModulePromise;
}

function teardownHead(head, container) {
  try {
    if (head && typeof head.stop === 'function') {
      head.stop();
    }
    if (head && typeof head.destroy === 'function') {
      head.destroy();
    }
  } catch {
    // Ignore teardown errors from third-party renderer.
  }

  if (container) {
    container.innerHTML = '';
  }
}

function pickBrowserVoice(voices, speechLang, gender) {
  if (!Array.isArray(voices) || voices.length === 0) {
    return null;
  }

  const normalizedLang = String(speechLang || '').toLowerCase();
  const languageFamily = normalizedLang.split('-')[0];
  const genderHints =
    gender === 'male'
      ? ['male', 'david', 'daniel', 'alex', 'george', 'james', 'lee']
      : ['female', 'zira', 'samantha', 'victoria', 'karen', 'ava', 'aria'];

  const sameLanguageVoices = voices.filter(
    (voice) => String(voice.lang || '').toLowerCase() === normalizedLang
  );
  const familyVoices = voices.filter((voice) =>
    String(voice.lang || '').toLowerCase().startsWith(`${languageFamily}-`)
  );
  const candidateVoices = sameLanguageVoices.length ? sameLanguageVoices : familyVoices;

  const genderMatchedVoice = candidateVoices.find((voice) => {
    const lowerName = String(voice.name || '').toLowerCase();
    return genderHints.some((hint) => lowerName.includes(hint));
  });

  if (genderMatchedVoice) {
    return genderMatchedVoice;
  }

  return candidateVoices[0] || voices[0] || null;
}

function speakWithBrowserTts(text) {
  if (
    typeof window === 'undefined' ||
    !window.speechSynthesis ||
    typeof window.SpeechSynthesisUtterance !== 'function'
  ) {
    return;
  }

  const voiceConfig = getTalkingHeadVoiceConfig();
  const utterance = new window.SpeechSynthesisUtterance(text);
  utterance.lang = voiceConfig.speechLang;
  const voices = window.speechSynthesis.getVoices?.() || [];
  const selectedVoice = pickBrowserVoice(voices, voiceConfig.speechLang, voiceConfig.gender);
  if (selectedVoice) {
    utterance.voice = selectedVoice;
  }
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
}

// Props: `onReady(ok: boolean)` fires once the avatar has loaded (true) or
// given up (false, browser TTS will be used) so callers can defer speech.
const TalkingHeadAvatar = forwardRef((props, ref) => {
  const avatarRef = useRef(null);
  const retryTimerRef = useRef(null);
  const headRef = useRef(null);
  // Bumped on every (re)load and on unmount; a load whose generation is no
  // longer current has been superseded (e.g. the caregiver switched avatars).
  const loadGenerationRef = useRef(0);
  const onReadyRef = useRef(props.onReady);
  onReadyRef.current = props.onReady;
  const onLoadingRef = useRef(props.onLoading);
  onLoadingRef.current = props.onLoading;
  const { avatarSelection } = props;
  // True only once showAvatar() has resolved; the TalkingHead instance exists
  // before that but cannot speak yet.
  const avatarLoadedRef = useRef(false);
  // TalkingHead fetches TTS audio asynchronously; stopSpeaking() cannot cancel
  // a request already in flight, so that line would still play when it
  // arrives. After a stop, a short watchdog re-stops any audio that starts
  // without a new speak request.
  const speakGenerationRef = useRef(0);
  const stopWatchdogRef = useRef(null);
  const STOP_WATCHDOG_MS = 6000;
  const STOP_WATCHDOG_INTERVAL_MS = 120;
  const notifyReady = (ok) => {
    avatarLoadedRef.current = ok;
    if (typeof onReadyRef.current === 'function') {
      onReadyRef.current(ok);
    }
  };

  const [loadingText, setLoadingText] = useState('Loading avatar...');
  const [webglSupported, setWebglSupported] = useState(true);

  // The selected avatar ID resolved through the catalog (model file, body
  // type, per-avatar eye settings). Unknown IDs load the default avatar.
  const avatarConfig = useMemo(() => {
    const avatar = resolveAvatar(avatarSelection ?? readStoredAvatar());
    return { ...avatar, url: buildAvatarUrl(avatar.url) };
  }, [avatarSelection]);

  useImperativeHandle(ref, () => ({
    // Whether the 3D avatar can speak (loaded) — otherwise browser TTS is used.
    get isLoaded() {
      return avatarLoadedRef.current && !!headRef.current;
    },
    speakText: (text) => {
      speakGenerationRef.current += 1;
      clearInterval(stopWatchdogRef.current);
      if (avatarLoadedRef.current && headRef.current && typeof headRef.current.speakText === 'function') {
        headRef.current.speakText(text);
        return;
      }

      speakWithBrowserTts(text);
    },
    // Cuts off whatever is being spoken (avatar queue and browser TTS).
    stopSpeaking: () => {
      speakGenerationRef.current += 1;
      const generation = speakGenerationRef.current;
      const stopHead = () => {
        if (headRef.current && typeof headRef.current.stopSpeaking === 'function') {
          try {
            headRef.current.stopSpeaking();
          } catch (err) {
            console.warn('Avatar stopSpeaking failed:', err);
          }
        }
      };
      stopHead();

      clearInterval(stopWatchdogRef.current);
      const startedAt = Date.now();
      stopWatchdogRef.current = setInterval(() => {
        const head = headRef.current;
        if (generation !== speakGenerationRef.current || !head || Date.now() - startedAt > STOP_WATCHDOG_MS) {
          clearInterval(stopWatchdogRef.current);
          return;
        }
        const lateAudio =
          head.isSpeaking === true ||
          head.isAudioPlaying === true ||
          (Array.isArray(head.audioPlaylist) && head.audioPlaylist.length > 0);
        if (lateAudio) {
          stopHead();
        }
      }, STOP_WATCHDOG_INTERVAL_MS);
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    },
    get audioCtx() {
      return headRef.current?.audioCtx;
    },
    // True while the avatar is talking or still has queued speech (the TTS
    // request for a line takes a moment before audio actually starts).
    get isSpeaking() {
      const head = headRef.current;
      if (!head) {
        return false;
      }
      return head.isSpeaking === true || (Array.isArray(head.speechQueue) && head.speechQueue.length > 0);
    },
  }));

  useEffect(() => {
    loadGenerationRef.current += 1;
    const generation = loadGenerationRef.current;
    const isCancelled = () => loadGenerationRef.current !== generation;
    const containerElement = avatarRef.current;

    avatarLoadedRef.current = false;
    setLoadingText('Loading avatar...');
    if (typeof onLoadingRef.current === 'function') {
      onLoadingRef.current();
    }

    const waitBeforeRetry = (ms) => {
      return new Promise((resolve) => {
        retryTimerRef.current = setTimeout(() => {
          retryTimerRef.current = null;
          resolve();
        }, ms);
      });
    };

    const onProgress = (event) => {
      if (!event?.lengthComputable || isCancelled()) {
        return;
      }

      const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
      setLoadingText(`Loading avatar... ${progress}%`);
    };

    const loadAvatarWithRetry = async () => {
      const webglAvailable = hasWebGLSupport();
      setWebglSupported(webglAvailable);

      if (!webglAvailable) {
        setLoadingText('3D avatar unavailable: WebGL is disabled in this browser.');
        notifyReady(false);
        return;
      }

      let lastError = null;

      for (let attempt = 1; attempt <= MAX_AVATAR_LOAD_ATTEMPTS; attempt += 1) {
        if (isCancelled()) {
          return;
        }

        let localHead = null;

        try {
          const attemptLabel =
            attempt === 1
              ? 'Loading avatar...'
              : `Retrying avatar load (${attempt}/${MAX_AVATAR_LOAD_ATTEMPTS})...`;
          setLoadingText(attemptLabel);

          const { TalkingHead } = await loadTalkingHeadModule();
          if (isCancelled() || !containerElement) {
            return;
          }

          teardownHead(headRef.current, containerElement);
          headRef.current = null;

          localHead = new TalkingHead(containerElement, {
            ttsEndpoint: 'https://eu-texttospeech.googleapis.com/v1beta1/text:synthesize',
            ttsApikey: 'AIzaSyDSkk-2DrYGYe1IStMudAx_GJz9Va7kT6Y',
            lipsyncModules: ['en', 'fi'],
            cameraView: 'upper',
            // Keep the avatar facing the caregiver: no random head turns, and
            // always hold eye contact (TalkingHead defaults are 0.5 / 0.2-0.5).
            avatarIdleHeadMove: 0,
            avatarSpeakingHeadMove: 0,
            avatarIdleEyeContact: 1,
            avatarSpeakingEyeContact: 1,
          });

          headRef.current = localHead;
          if (process.env.NODE_ENV === 'development') {
            // Console access for inspecting the avatar while developing.
            window.__vicaHead = localHead;
          }
          const voiceConfig = getTalkingHeadVoiceConfig();

          await localHead.showAvatar(
            {
              url: avatarConfig.url,
              body: avatarConfig.body,
              avatarMood: 'neutral',
              ttsLang: voiceConfig.ttsLang,
              ttsVoice: voiceConfig.ttsVoice,
              lipsyncLang: 'en',
            },
            onProgress
          );

          if (isCancelled()) {
            teardownHead(localHead, containerElement);
            return;
          }

          // The neutral mood sways the upper body (and the head with it) up to
          // ~17 degrees left/right, and every spoken line starts with a
          // "look at camera" move that leans it forward/back by an angle
          // computed from the rig's spine posture (on Xavier it reads as
          // lifting his head). Pin all three so the avatar faces forward;
          // blinking, eye glances and lip-sync still keep it alive.
          // Per-avatar overrides come from fixedMorphs in avatarCatalog.js.
          const fixedMorphs = {
            ...STEADY_POSE_MORPHS,
            ...avatarConfig.fixedMorphs,
          };
          Object.entries(fixedMorphs).forEach(([morph, value]) => {
            localHead.setFixedValue(morph, value);
          });

          setLoadingText('');
          notifyReady(true);
          return;
        } catch (error) {
          if (isCancelled()) {
            // Superseded: headRef may already hold the newer avatar.
            teardownHead(localHead, containerElement);
            return;
          }
          lastError = error;
          console.warn(`Avatar load attempt ${attempt} failed.`, error);

          if (containerElement) {
            teardownHead(localHead || headRef.current, containerElement);
          }
          headRef.current = null;

          if (isUnsupportedTalkingHeadAvatarError(error)) {
            console.warn('Selected avatar is not rigged for TalkingHead.');
            setLoadingText('Selected avatar is incompatible. Switched to default avatar.');
            notifyReady(false);
            // Loads the default avatar (and its name) in place.
            storeSelectedAvatar(DEFAULT_AVATAR_ID);
            return;
          }

          if (attempt < MAX_AVATAR_LOAD_ATTEMPTS) {
            await waitBeforeRetry(RETRY_DELAYS_MS[attempt - 1] || 1200);
          }
        }
      }

      console.error('Avatar failed to load after retries:', lastError);
      setLoadingText('Error loading avatar. Check connection and try refreshing.');
      notifyReady(false);
    };

    loadAvatarWithRetry();

    return () => {
      loadGenerationRef.current += 1;
      clearInterval(stopWatchdogRef.current);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      if (containerElement) {
        teardownHead(headRef.current, containerElement);
      }
      headRef.current = null;
    };
  }, [avatarConfig]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {webglSupported ? (
        <div ref={avatarRef} style={{ width: '100%', height: '100%' }} />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#f2f8fc',
            fontWeight: 700,
            fontSize: '1rem',
            padding: '1.25rem',
            background:
              'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2), transparent 45%), linear-gradient(145deg, rgba(5,64,95,0.75), rgba(9,106,160,0.65))',
          }}
        >
          3D avatar preview is unavailable on this device.
        </div>
      )}
      {webglSupported && loadingText && (
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: 10,
            right: 10,
            textAlign: 'center',
            color: 'white',
          }}
        >
          {loadingText}
        </div>
      )}
    </div>
  );
});

export default TalkingHeadAvatar;
